/**
 * "Open as company" hand-off between the admin portal and the app tab
 * (backend: job_scraper/impersonation.py).
 *
 * The portal opens the session, then hands its login payload to a NEW tab
 * through localStorage under a one-time random key (same origin), and the
 * tab removes it immediately. Nothing goes in the URL except that key, so the
 * token never lands in history, logs or a Referer header. The app tab keeps
 * the session in sessionStorage - it dies with the tab.
 */
const HANDOFF_PREFIX = "haire-open-as:";
const SESSION_KEY = "haire-open-as-session";
const HANDOFF_MAX_AGE_MS = 2 * 60 * 1000;

const randomKey = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

/** Portal side: stash the payload, return the one-time key. */
export function stashOpenAs(payload) {
  const key = randomKey();
  window.localStorage.setItem(
    HANDOFF_PREFIX + key,
    JSON.stringify({ ...payload, at: Date.now() }),
  );
  return key;
}

/** App side: read and delete the payload (null when missing or stale). */
export function takeOpenAs(key) {
  if (!key || !/^[a-f0-9]{32}$/.test(key)) return null;
  const storageKey = HANDOFF_PREFIX + key;
  let raw = null;
  try {
    raw = window.localStorage.getItem(storageKey);
  } catch {
    raw = null;
  }
  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // storage unavailable: nothing to clean up
  }
  if (!raw) return null;
  try {
    const data = JSON.parse(raw);
    return Date.now() - (data.at || 0) > HANDOFF_MAX_AGE_MS ? null : data;
  } catch {
    return null;
  }
}

export function setOpenAsSession(session) {
  try {
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // private mode: the banner simply won't show
  }
}

export function getOpenAsSession() {
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearOpenAsSession() {
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // nothing to clear
  }
}
